import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function BranchManagement() {
  const authCtx = useContext(AuthContext);

  if (!authCtx.checkRight('branch:read:own') && !authCtx.checkRight('branch:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const branchSchema = {
    locationName: {
      label: 'Standort',
      type: 'text',
      required: true,
      minWidth: '180px',
    },
    longName: {
      label: 'Vollständiger Name',
      type: 'text',
      required: true,
      minWidth: '220px',
    },
    adyenMerchant: {
      label: 'Adyen Merchant',
      type: 'text',
      required: false,
      minWidth: '200px',
    },
    regiondoAPIKey: {
      label: 'Regiondo API Key',
      type: 'text',
      required: false,
      readOnly: true,
      excludeFromAdd: true,
      minWidth: '220px',
    },
    timebutlerBranch: {
      label: 'Timebutler Branch',
      type: 'text',
      required: false,
      minWidth: '200px',
    },
    paymentTermsDays: {
      label: 'Zahlungsziel (Tage)',
      type: 'number',
      required: false,
      minWidth: '160px',
    },
    legalEntity: {
      label: 'Rechtsträger',
      type: 'select',
      api: {
        fieldName: 'legalEntity',
        itemField: 'legalEntity',
        idName: '_id',
        resource: 'legalEntity',
        labelField: 'legalName',
        labelBuilder: item =>
          `${item.legalName || ''}${
            item.street ? ` – ${item.street} ${item.houseNumber || ''}` : ''
          }${
            item.postcode || item.city ? `, ${item.postcode || ''} ${item.city || ''}` : ''
          }`.trim(),
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Rechtsträger auswählen',
      required: false,
      minWidth: '180px',
    },
    noShowKulanzVoucherProductId: {
      label: 'No-Show Kulanz-Gutscheinprodukt',
      type: 'select',
      api: {
        fieldName: 'noShowKulanzVoucherProductId',
        itemField: 'noShowKulanzVoucherProductId',
        idName: '_id',
        resource: 'voucherProduct',
        labelField: 'name',
        labelBuilder: item => item.name || '',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Gutscheinprodukt auswählen',
      required: false,
      excludeFromAdd: true,
      minWidth: '220px',
    },
    monthlyGiftedVoucherBudgetEUR: {
      label: 'Monatsbudget Kulanz/Marketing (€)',
      type: 'number',
      required: false,
      minWidth: '240px',
      description: 'Monatliches Budget fuer Kulanz-/Marketing-Gutscheine (Standard: 300 €)',
    },
    addresses: {
      label: 'Adressen',
      type: 'nested',
      addButtonText: 'Neue Adresse hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'addresses',
        nestedEndpoint: 'address',
      },
      schema: {
        unitDescription: {
          label: 'Einheit/Beschreibung',
          type: 'text',
          required: true,
          minWidth: '200px',
        },
        street: {
          label: 'Straße',
          type: 'text',
          required: true,
          minWidth: '180px',
        },
        houseNumber: {
          label: 'Hausnummer',
          type: 'text',
          required: true,
          minWidth: '100px',
        },
        postcode: {
          label: 'PLZ',
          type: 'text',
          required: true,
          minWidth: '100px',
        },
        city: {
          label: 'Ort',
          type: 'text',
          required: true,
          minWidth: '160px',
        },
      },
    },
    invoiceCounters: {
      label: 'Rechnungszähler',
      type: 'nested',
      addButtonText: 'Neuen Zähler hinzufügen',
      excludeFromAdd: true,
      readOnly: true,
      api: {
        itemField: 'invoiceCounters',
        nestedEndpoint: 'invoiceCounter',
      },
      schema: {
        year: {
          label: 'Jahr',
          type: 'number',
          required: true,
          minWidth: '100px',
        },
        currentNumber: {
          label: 'Aktuelle Nummer',
          type: 'number',
          required: true,
          minWidth: '140px',
        },
      },
    },
    invoiceCode: {
      label: 'Rechnungskürzel',
      type: 'text',
      required: true,
      readOnly: true,
      minWidth: '80px',
    },
    copagoCashbookSettings: {
      label: 'Copago Kassenbuch-Einstellungen',
      type: 'nested',
      addButtonText: 'Neue Einstellung hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'copagoCashbookSettings',
        nestedEndpoint: 'copagoCashbookSetting',
      },
      schema: {
        copagoBranch: {
          label: 'Copago Branch',
          type: 'number',
          required: true,
          minWidth: '140px',
        },
        subName: {
          label: 'Sub-Name',
          type: 'text',
          required: false,
          minWidth: '160px',
        },
        costCenter: {
          label: 'Kostenstelle',
          type: 'number',
          required: false,
          minWidth: '140px',
        },
        cashAccount: {
          label: 'Kassenkonto',
          type: 'number',
          required: false,
          minWidth: '140px',
        },
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={branchSchema}
      title='Neue Filiale anlegen'
      resourceType='branch'
      headerSearchPlaceholder='Filialen durchsuchen'
      parentIsEditable={authCtx.checkRight('branch:update')}
      options={{
        titleKey: 'longName',
      }}
      visibleColumnKeys={[
        'longName',
        'locationName',
        'adyenMerchant',
        'monthlyGiftedVoucherBudgetEUR',
        'legalEntity',
        'addresses',
        'invoiceCode',
      ]}
    />
    </Box>
  );
}

export default BranchManagement;
